import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { throwForValidationResult } from "@/lib/graphql-error";
import {
  CreateExpertiseDocument,
  DeleteExpertiseDocument,
  MyselfExpertiseDataDocument,
  UpdateExpertiseDocument,
  type MyselfExpertiseDataQuery,
} from "@/graphql/generated/operations";
import { audienceRelations, type Audience } from "@/lib/audience";

// "Expertise skill" disambiguates the genesis Expertise entity (free-form
// skill descriptors with level + position) from the broader "expertise"
// page domain that also includes experience, education, etc.
export interface SkillFormInput {
  description: string;
  position: number;
  level: number;
  sharedWith?: ReadonlyArray<Audience | string>;
}

export type SkillItem = NonNullable<
  NonNullable<MyselfExpertiseDataQuery["myself"]>["expertises"]
>[number];

export interface UseExpertiseSkillsResult {
  items: readonly SkillItem[];
  add: (input: SkillFormInput) => Promise<void>;
  update: (id: string, input: SkillFormInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  busy: boolean;
  error: Error | null;
}

function toDataInput(input: SkillFormInput) {
  return {
    description: input.description,
    position: input.position,
    level: input.level,
    ...(input.sharedWith !== undefined
      ? { sharedWith: audienceRelations(input.sharedWith) }
      : {}),
  };
}

export function useExpertiseSkills(): UseExpertiseSkillsResult {
  const intl = useIntl();
  const query = useQuery(MyselfExpertiseDataDocument);
  const personId = query.data?.myself?.id;

  // A blank name is refused on CREATE by the panel gates, but clearing it on an EXISTING row went
  // through: nothing in spark threw and ExpertiseController validates nothing at all, so the row
  // persisted blank, rendered as "–", and the panel auto-closed as if it had worked.
  function requireDescription(input: SkillFormInput) {
    if (!String(input.description ?? "").trim()) {
      throw new Error(
        intl.formatMessage({ id: "profile.page.error.skillNameRequired", defaultMessage: "A name is required." }),
      );
    }
  }

  const saveFailed = intl.formatMessage({ id: "profile.page.error.saveSkill", defaultMessage: "Could not save the skill" });

  const [createMut, createState] = useMutation(CreateExpertiseDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [updateMut, updateState] = useMutation(UpdateExpertiseDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [deleteMut, deleteState] = useMutation(DeleteExpertiseDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });

  async function add(input: SkillFormInput) {
    if (!personId) throw new Error("useExpertiseSkills: no personId yet");
    requireDescription(input);
    const created = await createMut({
      variables: {
        input: { data: { ...toDataInput(input), person: { id: personId } } },
      },
    });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = created.data?.createExpertise;
    if (out?.__typename !== "CreateExpertiseResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function update(id: string, input: SkillFormInput) {
    requireDescription(input);
    const updated = await updateMut({ variables: { id, input: { data: toDataInput(input) } } });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = updated.data?.updateExpertise;
    if (out?.__typename !== "UpdateExpertiseResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function remove(id: string) {
    await deleteMut({ variables: { input: { id } } });
  }

  return {
    items: query.data?.myself?.expertises ?? [],
    add,
    update,
    remove,
    busy: createState.loading || updateState.loading || deleteState.loading,
    error:
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
  };
}
